import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Building, Edit, Info, Plus, Printer, Shield, Target, Trash2, Users, X } from 'lucide-react';
import { Button } from './ui/button';
import { API_BASE_URL } from '../config';
import SortableEntryList from './SortableEntryList';
import { writeSanitizedHTML } from '../utils/safePrint';

const API = `${API_BASE_URL}/api`;

const SituationUnitModule = ({ theme, exerciseId }) => {
  const [situationData, setSituationData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);

  // Helper: 24-hour timestamp (e.g. "2024-10-27 23:15")
  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('response');

  // Form state
  const [formData, setFormData] = useState({
    category: 'response',
    title: '',
    description: '',
    priority: 'medium',
    status: 'active',
    timestamp: '',
    location: '',
    contact_info: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  // Categories and their fields based on IC data inputs
  const categories = {
    'incident-overview': {
      label: 'Incident Overview',
      icon: Info,
      fields: [
        { key: 'incident_name', label: 'Incident Name', type: 'text', required: true },
        { key: 'incident_number', label: 'Incident Number', type: 'text', required: true },
        { key: 'date_time_prepared', label: 'Date and Time Prepared/Initiated', type: 'datetime-local', required: true },
        { key: 'situation_summary', label: 'Situation Summary', type: 'textarea', required: true, placeholder: 'Narrative describing current conditions and potential hazards' }
      ]
    },
    'safety-briefing': {
      label: 'Safety Briefing',
      icon: Shield,
      fields: [
        { key: 'safety_briefing', label: 'Safety Briefing', type: 'textarea', required: true, placeholder: 'Key safety briefing information' },
        { key: 'health_safety_hazards', label: 'Health and Safety Hazards', type: 'textarea', required: true, placeholder: 'Health and safety hazards for responders and the public' },
        { key: 'protective_measures', label: 'Protective Measures', type: 'textarea', required: true, placeholder: 'Protective measures and safety protocols' }
      ]
    },
    'objectives-actions': {
      label: 'Objectives & Actions',
      icon: Target,
      fields: [
        { key: 'current_objectives', label: 'Current Objectives', type: 'textarea', required: true, placeholder: 'Clear goals guiding the response, prioritizing life safety, stabilization, and property/environmental preservation' },
        { key: 'planned_objectives', label: 'Planned Objectives', type: 'textarea', required: false, placeholder: 'Planned objectives for future operational periods' },
        { key: 'current_actions', label: 'Current Actions', type: 'textarea', required: true, placeholder: 'Specific strategies and tactics currently being employed' },
        { key: 'planned_actions', label: 'Planned Actions', type: 'textarea', required: true, placeholder: 'Planned strategies and tactics for the next operational period' }
      ]
    },
    'resources-organization': {
      label: 'Resources & Organization',
      icon: Users,
      fields: [
        { key: 'personnel_equipment_status', label: 'Personnel and Equipment Status', type: 'textarea', required: true, placeholder: 'Status of personnel and equipment currently committed' },
        { key: 'committed_resources', label: 'Committed Resources', type: 'textarea', required: false, placeholder: 'Resources currently committed to operations' },
        { key: 'assigned_resources', label: 'Assigned Resources', type: 'textarea', required: false, placeholder: 'Resources assigned to specific tasks' },
        { key: 'enroute_resources', label: 'En Route Resources', type: 'textarea', required: false, placeholder: 'Resources currently en route to the incident' },
        { key: 'ordered_resources', label: 'Ordered Resources', type: 'textarea', required: false, placeholder: 'Resources that have been ordered but not yet arrived' },
        { key: 'onscene_organization', label: 'On-Scene Organization', type: 'textarea', required: true, placeholder: 'Current ICS structure and organizational setup' },
        { key: 'established_facilities', label: 'Established Facilities', type: 'textarea', required: false, placeholder: 'Facilities established for the incident response' }
      ]
    },
    'safety-status': {
      label: 'Safety Status',
      icon: AlertTriangle,
      fields: [
        { key: 'fatalities', label: 'Fatalities', type: 'text', required: false, placeholder: 'Number and details of fatalities' },
        { key: 'injuries', label: 'Injuries', type: 'text', required: false, placeholder: 'Number and details of injuries' },
        { key: 'trapped_individuals', label: 'Trapped Individuals', type: 'text', required: false, placeholder: 'Number and status of trapped individuals' },
        { key: 'evacuated_populations', label: 'Evacuated Populations', type: 'text', required: false, placeholder: 'Number and details of evacuated populations' }
      ]
    },
    'jurisdictions-media': {
      label: 'Jurisdictions & Media',
      icon: Building,
      fields: [
        { key: 'jurisdictions_involved', label: 'Jurisdictions Involved', type: 'textarea', required: true, placeholder: 'All jurisdictions involved in the response' },
        { key: 'organizations_involved', label: 'Organizations Involved', type: 'textarea', required: true, placeholder: 'All organizations and agencies contributing to the response' },
        { key: 'contributing_agencies', label: 'Contributing Agencies', type: 'textarea', required: false, placeholder: 'Agencies and parties contributing to the response' },
        { key: 'media_interests', label: 'Media Interests', type: 'textarea', required: false, placeholder: 'Relevant media attention or coverage' },
        { key: 'public_information_requirements', label: 'Public Information Requirements', type: 'textarea', required: false, placeholder: 'Public information requirements and protocols' }
      ]
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'critical', label: 'Critical', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'active', label: 'Active', color: 'text-green-500' },
    { value: 'pending', label: 'Pending', color: 'text-yellow-500' },
    { value: 'completed', label: 'Completed', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-500' }
  ];

  // Load situation unit data on component mount
  useEffect(() => {
    loadSituationData();
  }, [exerciseId]);

  const loadSituationData = async () => {
    if (!exerciseId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/situation-unit?exercise_id=${exerciseId}`);
      setSituationData(response.data);
    } catch (error) {
      console.error('Error loading situation unit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      const submissionData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: new Date().toISOString()
      };

      if (editingEntry) {
        await axios.put(`${API}/situation-unit/${editingEntry.id}`, submissionData);
      } else {
        await axios.post(`${API}/situation-unit`, submissionData);
      }

      await loadSituationData();
      resetForm();
      setShowForm(false);
    } catch (error) {
      console.error('Error saving situation unit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      category: entry.category,
      title: entry.title,
      description: entry.description,
      priority: entry.priority,
      status: entry.status,
      timestamp: entry.timestamp ? new Date(entry.timestamp).toISOString().slice(0, -1) : '',
      location: entry.location || '',
      contact_info: entry.contact_info || '',
      notes: entry.notes || '',
      ...Object.fromEntries(
        Object.entries(entry).filter(([key]) => 
          !['id', 'exercise_id', 'created_at', 'updated_at', 'category', 'title', 'description', 'priority', 'status', 'timestamp', 'location', 'contact_info', 'notes'].includes(key)
        )
      )
    });
    setActiveCategory(entry.category);
    setShowForm(true);
  };

  const handleDelete = async (entry) => {
    if (window.confirm('Are you sure you want to delete this situation unit entry?')) {
      try {
        await axios.delete(`${API}/situation-unit/${entry.id}`);
        await loadSituationData();
      } catch (error) {
        console.error('Error deleting situation unit entry:', error);
      }
    }
  };

  const handlePrint = (entry = null) => {
    const printContent = entry ? 
      generateSingleEntryPrint(entry) : 
      generateMultipleEntriesPrint();
    
    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, `
      <html>
        <head>
          <title>Situation Unit Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 10px; }
            .entry { margin-bottom: 30px; border: 1px solid #ddd; padding: 15px; page-break-inside: avoid; }
            .field { margin-bottom: 8px; }
            .label { font-weight: bold; display: inline-block; width: 150px; }
            .category { background: #f5f5f5; padding: 10px; margin-bottom: 15px; border-left: 4px solid #007bff; }
            .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
            @media print {
              .entry { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          ${printContent}
          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>Powered by EXRSIM - Emergency Response Simulator</p>
          </div>
        </body>
      </html>
    `);
    printWindow.print();
  };

  const generateSingleEntryPrint = (entry) => {
    const category = categories[entry.category];
    return `
      <div class="header">
        <h1>Situation Unit Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
      </div>
      <div class="entry">
        <div class="category">
          <h3>${category?.label || entry.category}</h3>
        </div>
        <div class="field"><span class="label">Title:</span> ${entry.title || 'N/A'}</div>
        <div class="field"><span class="label">Description:</span> ${entry.description || 'N/A'}</div>
        <div class="field"><span class="label">Priority:</span> ${entry.priority || 'N/A'}</div>
        <div class="field"><span class="label">Status:</span> ${entry.status || 'N/A'}</div>
        <div class="field"><span class="label">Timestamp:</span> ${entry.timestamp ? format24HourTimestamp(entry.timestamp) : 'N/A'}</div>
        <div class="field"><span class="label">Location:</span> ${entry.location || 'N/A'}</div>
        <div class="field"><span class="label">Contact Info:</span> ${entry.contact_info || 'N/A'}</div>
        <div class="field"><span class="label">Notes:</span> ${entry.notes || 'None'}</div>
        ${category?.fields.map(field => 
          `<div class="field"><span class="label">${field.label}:</span> ${entry[field.key] || 'N/A'}</div>`
        ).join('') || ''}
      </div>
    `;
  };

  const generateMultipleEntriesPrint = () => {
    const selectedData = selectedEntries.length > 0 ? 
      situationData.filter(entry => selectedEntries.includes(entry.id)) : 
      situationData;
    
    return `
      <div class="header">
        <h1>Situation Unit Report</h1>
        <h3>Exercise: ${exerciseId}</h3>
        <p>${selectedData.length} entry(ies)</p>
      </div>
      ${selectedData.map(entry => generateSingleEntryPrint(entry)).join('')}
    `;
  };

  const validateForm = () => {
    const newErrors = {};
    const category = categories[formData.category];

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    category.fields.forEach(field => {
      if (field.required && !formData[field.key]?.trim()) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      category: 'response',
      title: '',
      description: '',
      priority: 'medium',
      status: 'active',
      timestamp: '',
      location: '',
      contact_info: '',
      notes: ''
    });
    setEditingEntry(null);
    setErrors({});
  };

  const handleCategoryChange = (categoryKey) => {
    setActiveCategory(categoryKey);
    setFormData(prev => ({ 
      ...prev, 
      category: categoryKey,
      // Reset category-specific fields
      ...Object.fromEntries(
        categories[categoryKey].fields.map(field => [field.key, ''])
      )
    }));
  };

  const filteredData = situationData.filter(entry => 
    activeCategory === 'all' || entry.category === activeCategory
  );

  const toggleSelectEntry = (entryId) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Situation Unit
            </h1>
            <p className="text-white text-sm">
              Manage incident command data inputs for comprehensive situational awareness and response coordination
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={() => {
                resetForm();
                setShowForm(!showForm);
              }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-blue-500 text-blue-500 hover:bg-blue-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
            {situationData.length > 0 && (
              <Button
                onClick={() => handlePrint()}
                variant="outline"
                className="border-gray-500 text-gray-500 hover:bg-gray-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print All
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Filter Buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(categories).map(([key, category]) => {
          const Icon = category.icon;
          return (
            <Button
              key={key}
              variant={activeCategory === key ? 'default' : 'outline'}
              onClick={() => setActiveCategory(key)}
              className="text-sm"
            >
              <Icon className="h-4 w-4 mr-2" />
              {category.label}
            </Button>
          );
        })}
        <Button
          variant={activeCategory === 'all' ? 'default' : 'outline'}
          onClick={() => setActiveCategory('all')}
          className="text-sm"
        >
          Show All
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-gray-800 rounded-lg shadow-lg p-6 mb-6 border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-orange-500">
              {editingEntry ? 'Edit Entry' : 'Add New Entry'}
            </h2>
            <Button
              onClick={() => {
                resetForm();
                setShowForm(false);
              }}
              variant="ghost"
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <form onSubmit={handleSubmitLocal} className="space-y-4">
            {/* Emergency Phase Selection */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">Emergency Phase</label>
              <select
                value={formData.category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
              >
                {Object.entries(categories).map(([key, category]) => (
                  <option key={key} value={key} className={category.color}>{category.label}</option>
                ))}
              </select>
            </div>

            {/* Basic Fields */}
            <div>
              <label className="block text-sm font-medium text-white mb-1">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                placeholder="Enter entry title"
              />
              {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Situation Summary</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                placeholder="Enter situation summary..."
              />
            </div>

            {/* Priority and Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {priorityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Category-specific Fields */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-white">{categories[formData.category]?.label} Details</h3>
              {categories[formData.category]?.fields.map(field => (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-white mb-1">
                    {field.label} {field.required && '*'}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.key] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    />
                  ) : field.type === 'datetime-local' ? (
                    <input
                      type="datetime-local"
                      value={formData[field.key] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                    />
                  ) : (
                    <input
                      type={field.type}
                      value={formData[field.key] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                      placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                    />
                  )}
                  {errors[field.key] && <p className="text-red-400 text-sm mt-1">{errors[field.key]}</p>}
                </div>
              ))}
            </div>

            {/* Optional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-white mb-1">Location</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  placeholder="Location or area"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-1">Contact Info</label>
                <input
                  type="text"
                  value={formData.contact_info}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_info: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                  placeholder="Contact information"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-1">Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-gray-700 text-white"
                placeholder="Additional notes or comments"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                {loading ? 'Saving...' : (editingEntry ? 'Update Entry' : 'Add Entry')}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setShowForm(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Data List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-orange-500">
            {activeCategory === 'all' ? 'All Entries' : categories[activeCategory]?.label + ' Entries'}
          </h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <p className={`${theme.colors.textSecondary} mt-2`}>Loading...</p>
          </div>
        ) : filteredData.length === 0 ? (
          <div className="p-8 text-center">
            <Info className={`h-12 w-12 ${theme.colors.textMuted} mx-auto mb-3`} />
            <p className={`${theme.colors.textSecondary}`}>
              No situation unit entries found
              {activeCategory !== 'all' && ` for ${categories[activeCategory]?.label}`}
            </p>
          </div>
        ) : (
          <div className="p-4">
            <SortableEntryList
              entries={filteredData}
              setEntries={(newEntries) => {
                if (activeCategory === 'all') {
                  setSituationData(newEntries);
                } else {
                  const otherEntries = situationData.filter(d => d.category !== activeCategory);
                  setSituationData([...otherEntries, ...newEntries]);
                }
              }}
              reorderEndpoint="situation-unit/reorder"
              renderEntry={(entry) => {
                const category = categories[entry.category];
                const Icon = category?.icon || Info;
                const priorityColor = priorityOptions.find(p => p.value === entry.priority)?.color || 'text-gray-500';
                const statusColor = statusOptions.find(s => s.value === entry.status)?.color || 'text-gray-500';

                return (
                  <div className="p-4 hover:bg-gray-800/50 bg-gray-800/30 rounded-lg border border-gray-700">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Icon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-white">{entry.title}</h3>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-blue-400">{category?.label}</span>
                              <span className={priorityColor}>{entry.priority}</span>
                              <span className={statusColor}>{entry.status}</span>
                              {entry.location && (
                                <span className={theme.colors.textMuted}>📍 {entry.location}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        {entry.description && (
                          <p className={`${theme.colors.textSecondary} text-sm mb-2 ml-8`}>
                            {entry.description}
                          </p>
                        )}
                        <div className={`text-xs ${theme.colors.textMuted} ml-8`}>
                          Created: {new Date(entry.created_at).toLocaleString()}
                          {entry.updated_at !== entry.created_at && (
                            <span> • Updated: {new Date(entry.updated_at).toLocaleString()}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedEntries.includes(entry.id)}
                          onChange={() => toggleSelectEntry(entry.id)}
                          className="rounded border-gray-600 bg-gray-700 text-orange-500 focus:ring-orange-500"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePrint(entry)}
                          className="text-green-400 hover:text-green-300"
                        >
                          <Printer className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(entry)}
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(entry)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ICS Form 221 module removed - functionality decommissioned
// Liaison Officer Module Component

export default SituationUnitModule;
